// import { useUser } from '../UserContext';
import CryptoJS from 'crypto-js';

// Export the interface
export interface FetchOptions extends Omit<RequestInit, "headers"> {
  skipAuth?: boolean;
  headers?: Record<string, string>;
  noResponseParse?: boolean;
  onTokenExpired?: () => void;
}

// Add a flag to track if we're already redirecting
let isRedirecting = false;

// Modified handleAuthRedirect
const handleAuthRedirect = () => {
  if (isRedirecting) return; // Prevent multiple redirects
  isRedirecting = true;

  // Get all current URL parameters
  const currentParams = new URLSearchParams(window.location.search);
  const loginParams = new URLSearchParams();

  // Copy all existing parameters
  currentParams.forEach((value, key) => {
    loginParams.append(key, value);
  });

  // Add a 'redirect' parameter with the current path if not already present
  if (!loginParams.has("redirect")) {
    loginParams.append("redirect", window.location.pathname);
  }

  // Construct the login URL with all parameters
  const loginUrl = `/login${
    loginParams.toString() ? `?${loginParams.toString()}` : ""
  }`;

  // Redirect to login
  window.location.href = loginUrl;
};

// function getCookie(name: string) {
//   const value = `; ${document.cookie}`;
//   const parts = value.split(`; ${name}=`);
//   if (parts.length === 2) return parts.pop()?.split(';').shift();
// }

// At the top of the file
let logoutHandler: ((expired: boolean) => void) | null = null;

export const setLogoutHandler = (handler: ((expired: boolean) => void) | null) => {
  logoutHandler = handler;
};

// Add refreshToken handler type
let refreshTokenHandler: (() => Promise<boolean>) | null = null;

export const setRefreshTokenHandler = (handler: (() => Promise<boolean>) | null) => {
  refreshTokenHandler = handler;
};

// Common fetch logic shared between both functions
const createFetchRequest = async (url: string, options: FetchOptions = {}) => {
  const { skipAuth = false, headers = {}, ...rest } = options;
  const finalUrl = url;

  if (isRedirecting) {
    throw new Error("Authentication redirect in progress");
  }

  const encryptedToken = sessionStorage.getItem("accessToken");
  const finalHeaders = new Headers();

  if (!(options.body instanceof FormData)) {
    finalHeaders.set("Content-Type", "application/json");
  }

  // Add CSRF token for non-GET requests
  //if (options.method && options.method !== 'GET') {
  //  finalHeaders.set('X-CSRFToken', getCookie('csrftoken') || '');
  //}

  Object.entries(headers).forEach(([key, value]) => {
    if (value !== null) {
      finalHeaders.set(key, value);
    }
  });

  if (!skipAuth && encryptedToken) {
    try {
      const decryptedToken = CryptoJS.AES.decrypt(
        encryptedToken,
        process.env.REACT_APP_ENCRYPTION_KEY || ""
      ).toString(CryptoJS.enc.Utf8);

      finalHeaders.set("Authorization", `Bearer ${decryptedToken}`);
    } catch (error) {
      console.error("Token decryption failed:", error);
      sessionStorage.removeItem("accessToken");
      handleAuthRedirect();
      throw new Error("Invalid authentication token");
    }
  }

  const response = await fetch(finalUrl, {
    headers: finalHeaders,
    ...rest,
  });

  // Handle specific response statuses
  if (response.status === 401) {
    if (refreshTokenHandler) {
      const refreshed = await refreshTokenHandler();
      if (refreshed) {
        return createFetchRequest(url, options);
      }
    }
    
    if (logoutHandler) {
      logoutHandler(true);
    } else {
      sessionStorage.removeItem("accessToken");
    }
    handleAuthRedirect();
    throw new Error("Authentication expired");
  }

  // For DELETE requests, 204 is a success
  if (response.status === 204) {
    return response;
  }

  // For other non-2xx responses, throw error
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response;
};

export const apiFetch = async (url: string, options: FetchOptions = {}) => {
  if (url.includes('undefined')) {
    throw new Error('Malformed URL: URL contains undefined');
  }

  const { noResponseParse, ...restOptions } = options;
  
  // Handle all URL cleaning in one place
  let finalUrl = url;
  
  // If not an absolute URL, handle relative URL construction
  if (!url.startsWith('http')) {
    // First, clean up the input URL by removing any leading /api/
    finalUrl = finalUrl.replace(/^\/?(api\/)+/, '');
    
    if (process.env.REACT_APP_USE_PROXY === 'true') {
      // In proxy mode, always prefix with single /api/
      finalUrl = `/api/${finalUrl}`;
    } else {
      // For non-proxy mode
      const cleanRoot = REACT_APP_API_ROOT.replace(/\/+$/, '');
      finalUrl = `${cleanRoot}/api/${finalUrl}`;
    }
  }

  const response = await createFetchRequest(finalUrl, restOptions);
  
  if (noResponseParse) {
    return response;
  }
  
  return response.json();
};

export const apiFetchNoResponseRequired = async (url: string, options: FetchOptions = {}) => {
  await createFetchRequest(url, options);
  return true; // Return true to indicate success
};

// Get the base API URL based on environment
export const getApiRoot = () => {
  if (process.env.REACT_APP_USE_PROXY === 'true') {
    // When using proxy, return the proxy path
    return process.env.REACT_APP_API_ROOT || '/api/';
  }
  
  // For non-proxy mode (local development), use the configured API root
  return process.env.REACT_APP_API_ROOT || 'https://local.api.culturemap.app:8443/';
};

// Use this in your API calls
export const REACT_APP_API_ROOT = getApiRoot();

// Get the OAuth URL based on environment
export const getOAuthRoot = () => {
  if (process.env.REACT_APP_USE_PROXY === 'true') {
    return '/o';  // Use the proxy path
  }
  
  // For non-proxy mode, use the API root
  return `${REACT_APP_API_ROOT}o`;
};

export const OAUTH_ROOT = getOAuthRoot();

// Helper function to get OAuth token endpoint
export const getOAuthTokenUrl = () => {
  if (process.env.REACT_APP_USE_PROXY === 'true') {
    return '/o/token/';
  }
  return `${getOAuthRoot()}/token/`;
};

// Helper function for OAuth token requests
export const fetchOAuthToken = async (formData: FormData) => {
  const tokenUrl = getOAuthTokenUrl();
  try {
    const response = await fetch(tokenUrl, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error(`OAuth token request failed: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    throw error;
  }
};

export const apiFetchWithRetry = async (url: string, options: FetchOptions = {}, maxRetries = 3, delay = 1000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await apiFetch(url, options);
    } catch (error: any) {
      // Check if it's a 502 error
      if (error.message?.includes('502')) {
        console.warn(`Attempt ${attempt}: Got 502 error, retrying...`);
        if (attempt === maxRetries) {
          throw new Error('Failed after max retries');
        }
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
        continue;
      }
      
      // For other errors, throw immediately
      throw error;
    }
  }
  throw new Error('Failed after max retries');
};

